import styled, { css } from 'styled-components/macro'
import { Swiper } from 'swiper/react'

import { Overlay, styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { motion } from 'framer-motion'

export const SliderContainer = styled.div`
  padding: 5rem 0 3rem;
  margin: 0 0 3rem;
  overflow: hidden;

  ${mq.smallOnly} {
    padding: 3rem 0;
  }

  ${mq.medium} {
    padding-bottom: 6rem;
  }

  ${mq.large} {
    .swiper {
      overflow: visible;
      background: var(--background);

      &::before,
      &::after {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 0;
        display: block;
        width: 20%;
        height: 5rem;
        mask: linear-gradient(to top, black, transparent);
        pointer-events: none;
        background: var(--background);
        z-index: 10000;
      }

      &::after {
        bottom: auto;
        top: 100%;
        mask: linear-gradient(to bottom, black, transparent);
      }
    }
  }
`

export const Navigation = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 3rem;
  margin-bottom: 2rem;

  button {
    ${styleReset}
    display: flex;
    align-items: center;
    margin: 0 2rem;
    width: 8rem;
    padding-right: 2rem;
    transition: padding 0.3s;

    &::before {
      content: '';
      display: block;
      flex: 1;
      margin: 0 -0.8rem;
      border-top: 2px solid;
    }

    ${mq.smallOnly} {
      width: 5rem;

      &::before {
        border-top-width: 1px;
      }

      svg {
        width: 1.5rem;
        height: auto;
      }
    }

    &:first-child {
      padding-left: 2rem;
      padding-right: 0;
      flex-direction: row-reverse;
    }

    &:disabled {
      opacity: 0.3;
    }

    &:not(:disabled) {
      cursor: pointer;

      ${mq.medium} {
        &:hover {
          padding: 0;
        }
      }
    }
  }
`

export const Title = styled(motion.h3)<{ $capitalize?: boolean }>`
  font-size: 2rem;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 auto;
  text-align: center;

  ${props =>
    props.$capitalize &&
    css`
      text-transform: uppercase;
    `}

  ${mq.medium} {
    font-size: 2.5rem;
  }

  ${mq.large} {
    font-size: 3.2rem;
  }
`

export const NavTitle = styled(Title)<{ $noHyphens?: boolean }>`
  font-size: 2rem;
  min-width: 7em;
  margin-bottom: -0.1em;

  ${mq.medium} {
    font-size: 2.5rem;
  }

  ${mq.large} {
    font-size: 3.2rem !important;
  }

  ${props =>
    props.$noHyphens &&
    css`
      hyphens: none;
    `}
`

export const Description = styled(motion.p)`
  margin: 0 auto;
  text-align: center;

  ${mq.smallOnly} {
    font-size: 1.2rem;
    line-height: 1.5;
  }
`

export type SlideProps = {
  aspectRatio?: string
  maximumWidth?: number
  maximumMobileWidth?: number
  imagesPerView?: number
  preferHeight?: boolean
}

export const SlideImage = styled.img<SlideProps>`
  display: block;
  width: 70vw;
  aspect-ratio: ${props => props.aspectRatio};
  object-fit: cover;
  cursor: pointer;

  ${mq.medium} {
    width: ${props =>
      `clamp(20rem, ${Math.round(100 / (props.imagesPerView || 1.33))}vw, ${
        (props.maximumWidth || 850) / 10
      }rem)`};
  }

  ${props =>
    props.preferHeight &&
    css`
      width: auto;
      height: 50vw;

      ${mq.medium} {
        height: 42rem;
      }
    `}
`

export const StyledSwiper = styled(Swiper)`
  .swiper-wrapper {
    align-items: center;
  }

  .swiper-slide {
    position: relative;
    width: max-content;

    a {
      &::before {
        ${Overlay}
        content: "";
        transition: backdrop-filter 0.4s;
      }

      &:hover::before {
        backdrop-filter: blur(5px);
      }
    }
  }
`

export const OverlayText = styled.span<{ $onlyOnHover?: boolean }>`
  ${Overlay}
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;

  ${props =>
    props.$onlyOnHover &&
    css`
      opacity: 0;
      transition: 0.5s all;
      transform: translateY(1rem);

      a:hover & {
        opacity: 1;
        transform: translate3d(0, 0, 0);
        transition-delay: 0.2s;
      }
    `}
`

export const OverlayTextInner = styled.span`
  display: block;
  width: min-content;
  color: #e4ddd4;
  font-size: 2.4rem;
  font-weight: 500;
  line-height: 0.89;
  text-transform: uppercase;

  ${mq.medium} {
    font-size: 3.5rem;
  }

  ${mq.large} {
    font-size: 5.3rem;
  }

  > span {
    line-height: 0.96;
    display: inline-block;

    &:first-child {
      width: max-content;

      &::after {
        content: '';
        display: inline-block;
        width: 2.83em;
        aspect-ratio: 149 / 63;
        mask: url("data:image/svg+xml,%3Csvg width='149' height='63' viewBox='0 0 149 63' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M136.399 12.2713C126.42 21.3246 113.073 25.2923 100.598 24.1465C100.362 24.0994 100.415 24.3154 100.494 24.3174L117.432 31.745L125.149 48.7382C125.149 48.7382 125.366 48.9932 125.399 48.6066C124.123 36.1286 127.707 22.5244 136.513 12.3842C136.513 12.3842 136.557 12.3164 136.513 12.273C136.469 12.2296 136.402 12.2742 136.402 12.2742L136.399 12.2713Z' fill='%23EAE5DF'/%3E%3Cpath d='M0 62H87.8395L125.485 24.1416' stroke='%23EAE5DF' stroke-width='2'/%3E%3C/svg%3E%0A")
          center center / contain no-repeat;
        background-color: currentColor;
      }
    }
  }
`

export const ImageBackdrop = styled(motion.button)`
  ${styleReset}
  ${Overlay}
  position: fixed;
  cursor: pointer;
  opacity: 0.8;
  background-color: var(--background-color);
  z-index: 1000;

  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    height: 3rem;
    border-left: 3px solid currentColor;
    top: 1.8rem;
    right: 3.5rem;
    transform: rotateZ(45deg);
  }

  &::after {
    transform: rotateZ(-45deg);
  }
`

export const ImagePopup = styled(motion.img)`
  display: block;
  position: fixed;
  top: 10vh;
  left: 10vw;
  width: 80vw;
  height: 80vh;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 5px 10px rgba(0, 0, 0, 0.2));
  z-index: 1001;
`

export const Note = styled(motion.p)`
  font-size: 1.1rem;
  text-align: center;
  margin: 0.6rem 0;
  ${mq.smallOnly} {
    line-height: 1.25;
  }
  ${mq.medium} {
    font-size: 1.4rem;
  }
`
